// =============================================================================
// Shared event-eligibility layer — the single source of truth Feed and Map
// both call into, so they can never silently disagree about whether a given
// event document should be visible.
//
// Built while investigating a regression where real, valid Dresden events
// (confirmed live in Firestore, confirmed passing every existing filter)
// were showing on the website but reporting "No mapped events nearby" in
// the app. Empirical simulation against real production documents (see the
// tests in __tests__/eventEligibility.test.ts, built from the exact shapes
// found) showed the query, approved/status checks, isEventActivity,
// isStudiZoneActivity, city resolution, "Today" date filtering, and
// coordinate extraction were ALL already correct against real data — 0
// unexpected rejections found. That means the regression is most likely a
// runtime-only condition this module can't fix by itself (a stale cached
// empty feed result, or a device clock/timezone/selected-city value that
// only exists at runtime) — but centralizing these checks here, with a
// diagnostic that explains exactly why any one event was rejected, is what
// makes that class of bug fast to confirm or rule out on a real device next
// time, instead of re-deriving the whole pipeline from scratch again.
// =============================================================================

import type { TonyteActivity } from '../types';
import { normalizeCityKey } from './cities';

type FirestoreTimestampLike = {
  seconds?: number;
  nanoseconds?: number;
  toDate?: () => Date;
};

// Normalizes Firestore Timestamps, ISO strings (with or without an explicit
// UTC offset), bare "YYYY-MM-DD" date-only strings, serialized
// `{ seconds, nanoseconds }` objects, epoch numbers, and already-a-Date
// values into one Date, or null if the value can't be parsed at all. A bare
// date-only string is anchored at LOCAL noon (not UTC midnight) so it can
// never silently shift to the previous/next calendar day depending on the
// device's timezone offset — the exact class of bug this investigation was
// checking for.
export function toDate(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as FirestoreTimestampLike).toDate === 'function') {
    const date = (value as FirestoreTimestampLike).toDate!();
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'object' && value !== null && 'seconds' in value) {
    const seconds = Number((value as FirestoreTimestampLike).seconds);
    return Number.isFinite(seconds) ? new Date(seconds * 1000) : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map(Number);
      return new Date(year, month - 1, day, 12, 0, 0, 0);
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// Device-local calendar-day key (getFullYear/Month/Date, never UTC methods)
// — every event in this app is Dresden-based, so this is only correct when
// the device's own clock/timezone is ALSO Dresden's; a device set to a
// different timezone will compute a different "today" than Dresden's actual
// today. See the module header — this was the #1 suspect for the reported
// regression and could not be ruled out (or confirmed) from server-side
// data alone.
export function toLocalDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toFiniteNumber(value: unknown): number | null {
  const numberValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(numberValue) ? numberValue : null;
}

function getCoordinatePair(value: unknown): { latitude: number; longitude: number } | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const latitude = toFiniteNumber(raw.lat ?? raw.latitude ?? raw._lat);
  const longitude = toFiniteNumber(raw.lng ?? raw.longitude ?? raw.lon ?? raw._long);
  if (latitude === null || longitude === null) return null;
  if (latitude === 0 && longitude === 0) return null; // "null island" — never a real venue
  return { latitude, longitude };
}

// Canonical coordinate resolver for the whole app — supports every shape
// currently in use across the real dataset: top-level lat/lng/latitude/
// longitude, a nested `coordinates` object (the shape every real Dresden
// event actually uses: `{ lat, lng }`), a `location` object (when it IS an
// object — real data also has `location` as a plain human-readable address
// STRING, which this safely ignores rather than throwing), `geo`,
// `geopoint`, and `point`. Also accepts a native Firestore GeoPoint (which
// exposes `.latitude`/`.longitude` getters) transparently, since
// getCoordinatePair checks those property names too.
export function getEventCoordinates(event: TonyteActivity): { latitude: number; longitude: number } | null {
  const raw = event as TonyteActivity & Record<string, unknown>;
  return (
    getCoordinatePair(raw)
    || getCoordinatePair(event.coordinates)
    || getCoordinatePair(raw.location)
    || getCoordinatePair(raw.geo)
    || getCoordinatePair(raw.geopoint)
    || getCoordinatePair(raw.point)
    || null
  );
}

export function hasValidCoordinates(event: TonyteActivity): boolean {
  return getEventCoordinates(event) !== null;
}

export function isEventApproved(event: TonyteActivity): boolean {
  return event.approved !== false && event.status !== 'rejected';
}

export function isEventPublished(event: TonyteActivity): boolean {
  return event.visibility !== 'private';
}

export function isEventInCity(event: TonyteActivity, cityKey: string | null | undefined): boolean {
  if (!cityKey) return true;
  const raw = event as TonyteActivity & Record<string, unknown>;
  return normalizeCityKey(String(raw.cityKey || raw.city || '')) === normalizeCityKey(cityKey);
}

type ParsedEventWindow = { startAt: Date | null; endAt: Date | null };

// Bulk event-import scripts (every city, every batch) write a plain
// "HH:MM" startTime/endTime pair alongside the date-only `date` field —
// they never set startAt/endAt. Without this, parseEventWindow's fallback
// below defaults to a synthetic noon-to-6pm window regardless of the
// event's real hours, which silently hid evening/overnight events (e.g. a
// 23:00-06:00 club night) well before they actually started or ended.
function parseTimeOfDay(value: unknown): { hours: number; minutes: number } | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

function combineDateAndTime(dateOnly: Date | null, time: { hours: number; minutes: number } | null): Date | null {
  if (!dateOnly || !time) return null;
  const combined = new Date(dateOnly);
  combined.setHours(time.hours, time.minutes, 0, 0);
  return combined;
}

function parseEventWindow(event: TonyteActivity): ParsedEventWindow {
  const raw = event as TonyteActivity & Record<string, unknown>;
  const explicitStart = toDate(raw.startAt) ?? toDate(raw.startsAt) ?? toDate(raw.startDateTime) ?? toDate(raw.timestamp);
  const explicitEnd = toDate(raw.endAt) ?? toDate(raw.endsAt) ?? toDate(raw.endDateTime);
  const dateOnly = toDate(event.startDate) ?? toDate(raw.date) ?? explicitStart;

  if (!dateOnly && !explicitStart) return { startAt: null, endAt: null };

  // Only consult startTime/endTime when there's no explicit start
  // timestamp — an event that already sets startAt keeps behaving exactly
  // as before.
  const startFromTimeFields = explicitStart ? null : combineDateAndTime(dateOnly, parseTimeOfDay(raw.startTime));
  const startAt = explicitStart ?? startFromTimeFields ?? dateOnly!;

  let endAt: Date;
  if (explicitEnd) {
    endAt = explicitEnd;
  } else if (startFromTimeFields) {
    const endTimeOfDay = parseTimeOfDay(raw.endTime);
    const candidateEnd = combineDateAndTime(dateOnly, endTimeOfDay);
    if (candidateEnd) {
      // Overnight event (e.g. 23:00 -> 06:00): an end time that's
      // numerically <= the start time actually lands on the next day.
      endAt = candidateEnd.getTime() <= startFromTimeFields.getTime()
        ? new Date(candidateEnd.getTime() + 24 * 60 * 60 * 1000)
        : candidateEnd;
    } else {
      endAt = new Date(startAt.getTime() + 6 * 60 * 60 * 1000);
    }
  } else {
    endAt = new Date(startAt.getTime() + 6 * 60 * 60 * 1000);
  }

  return { startAt, endAt };
}

// "Today" needs the event to still be ongoing (endAt >= now); any other
// selected date only needs the event's [startAt, endAt] window to overlap
// that calendar day at all.
export function isEventInDateRange(event: TonyteActivity, selectedDate: Date, now: Date = new Date()): boolean {
  const { startAt, endAt } = parseEventWindow(event);
  if (!startAt || !endAt) return false;

  const selectedKey = toLocalDateKey(selectedDate);
  const todayKey = toLocalDateKey(now);
  const overlapsSelectedDate = toLocalDateKey(startAt) <= selectedKey && toLocalDateKey(endAt) >= selectedKey;
  if (!overlapsSelectedDate) return false;

  if (selectedKey === todayKey) return endAt.getTime() >= now.getTime();
  return true;
}

export type EventEligibilityContext = {
  cityKey?: string | null;
  selectedDate?: Date;
  now?: Date;
};

// Dev-only diagnostic — call from Feed/Map when an event unexpectedly fails
// to appear, e.g.:
//   if (__DEV__) console.log('[Event eligibility]', event.id, explainEventRejection(event, { cityKey: selectedCityKey, selectedDate: selectedMapDate }));
// Returns an empty array when the event is fully eligible.
export function explainEventRejection(event: TonyteActivity, context: EventEligibilityContext = {}): string[] {
  const reasons: string[] = [];
  const now = context.now ?? new Date();

  if (!isEventPublished(event)) reasons.push('not-published');
  if (!isEventApproved(event)) reasons.push('not-approved');
  if (!isEventInCity(event, context.cityKey)) reasons.push('city-mismatch');
  if (context.selectedDate && !isEventInDateRange(event, context.selectedDate, now)) reasons.push('date-mismatch');
  if (!hasValidCoordinates(event)) reasons.push('invalid-coordinates');

  return reasons;
}
